import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["hidden", "trigger", "display", "clear", "panel", "search", "tree", "node", "option", "empty"]
  static values = { placeholder: String, separator: { type: String, default: " › " } }

  connect() {
    this.boundClose = this.closeOnClickOutside.bind(this)
    this.boundEscape = this.closeOnEscape.bind(this)
    document.addEventListener("click", this.boundClose)
    document.addEventListener("keydown", this.boundEscape)
  }

  disconnect() {
    document.removeEventListener("click", this.boundClose)
    document.removeEventListener("keydown", this.boundEscape)
  }

  toggle(event) {
    event.stopPropagation()
    const willOpen = this.panelTarget.hidden
    this.panelTarget.hidden = !willOpen
    this.triggerTarget.setAttribute("aria-expanded", willOpen ? "true" : "false")
    if (willOpen) {
      this.searchTarget.focus()
    } else {
      this.searchTarget.value = ""
      this.resetFilter()
    }
  }

  close() {
    if (this.panelTarget.hidden) return
    this.panelTarget.hidden = true
    this.triggerTarget.setAttribute("aria-expanded", "false")
    this.searchTarget.value = ""
    this.resetFilter()
  }

  select(event) {
    event.preventDefault()
    event.stopPropagation()
    const button = event.currentTarget
    const id = button.dataset.id
    const path = button.dataset.path

    this.hiddenTarget.value = id
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.displayTarget.textContent = path
    this.displayTarget.title = path
    this.displayTarget.classList.remove("text-outline/60")
    this.displayTarget.classList.add("text-on-surface")
    if (this.hasClearTarget) this.clearTarget.hidden = false

    this.close()
  }

  clear(event) {
    event.preventDefault()
    event.stopPropagation()
    this.hiddenTarget.value = ""
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.displayTarget.textContent = this.placeholderValue
    this.displayTarget.removeAttribute("title")
    this.displayTarget.classList.add("text-outline/60")
    this.displayTarget.classList.remove("text-on-surface")
    this.clearTarget.hidden = true
  }

  filter() {
    const query = this.searchTarget.value.toLowerCase().trim()
    if (query === "") return this.resetFilter()

    // Hide every node, collapse every <details>.
    this.nodeTargets.forEach((node) => { node.hidden = true })
    this.element.querySelectorAll("details").forEach((d) => { d.open = false })

    let visibleCount = 0

    // Show matches and expand their ancestors.
    this.nodeTargets.forEach((node) => {
      const name = (node.dataset.name || "").toLowerCase()
      if (!name.includes(query)) return
      node.hidden = false
      visibleCount++
      let el = node.parentElement
      while (el && el !== this.treeTarget) {
        if (el.tagName === "DETAILS") el.open = true
        if (el.tagName === "LI") el.hidden = false
        el = el.parentElement
      }
    })

    // Hide groups (root <li>) that have no visible descendant.
    this.treeTarget.querySelectorAll(":scope > li").forEach((li) => {
      const hasVisible = li.querySelector("[data-hierarchical-select-target~='node']:not([hidden])")
      const isSelfMatch = li.matches("[data-hierarchical-select-target~='node']") && !li.hidden
      li.hidden = !(hasVisible || isSelfMatch)
    })

    this.emptyTarget.hidden = visibleCount > 0
  }

  resetFilter() {
    this.nodeTargets.forEach((n) => { n.hidden = false })
    this.treeTarget.querySelectorAll(":scope > li").forEach((li) => { li.hidden = false })
    this.element.querySelectorAll("details").forEach((d) => { d.open = false })
    this.emptyTarget.hidden = true
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  closeOnEscape(event) {
    if (event.key === "Escape") this.close()
  }
}
